import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import { cacheGet, cacheSet } from '../../services/apiCache';
import { useTenant } from '../../context/TenantContext';

// ─── Parse de criterioAceitacao da classe como EMP ───────────────────────────
function parseCriterioEmp(criterio) {
  if (!criterio) return null;
  const pct = criterio.match(/±?(\d+(?:[.,]\d+)?)\s*%/);
  if (pct) return { empTipo: 'percentage', empValor: pct[1].replace(',', '.') };
  const num = criterio.match(/±?(\d+(?:[.,]\d+)?)/);
  if (num) return { empTipo: 'maxError', empValor: num[1].replace(',', '.') };
  return { empTipo: 'text', empValor: criterio };
}

// ─── Constantes ───────────────────────────────────────────────────────────────
const RESULTADOS = [
  { valor: 'aprovado', rotulo: 'Aprovado' },
  { valor: 'reprovado', rotulo: 'Reprovado' },
  { valor: 'aprovado_com_restricao', rotulo: 'Aprovado com Restrição' },
];

const ACREDITACOES = [
  { valor: 'RBC', rotulo: 'RBC (Rede Brasileira de Calibração)' },
  { valor: 'nao_RBC', rotulo: 'Não-RBC' },
  { valor: 'interno', rotulo: 'Laboratório Interno' },
];

const COR_CONFIANCA = {
  alta:  { dot: 'bg-emerald-400', texto: 'text-emerald-400', rotulo: 'Alta' },
  media: { dot: 'bg-amber-400',   texto: 'text-amber-400',   rotulo: 'Média' },
  baixa: { dot: 'bg-red-400',     texto: 'text-red-400',     rotulo: 'Baixa' },
};

const LABEL_RESULTADO_IA = {
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  aprovado_com_restricao: 'Aprovado com Restrição',
};

// ─── Estado inicial ───────────────────────────────────────────────────────────
function padraoVazio() {
  return { padraoReferenciaId: null, descricao: '', numeroCertificado: '', validoAte: '', incertezaExpandida: '', fatorK: '2' };
}

const estadoInicial = {
  instrumentId: '',
  calibrationType: 'externa',
  data: '',
  proximaData: '',
  resultado: '',
  fornecedor: '',
  acreditacaoFornecedor: '',
  numeroCertificado: '',
  observacoes: '',
  statusLaudo: 'rascunho',
  procedimentoId: '',
  gruposMedicao: [],
  padroesUsados: [padraoVazio()],
};

// ─── Cálculo GUM — aceita um padrão ou array de padrões (combina u_B via √Σu_Bᵢ²) ──
function calcularGUM(uA, padroes) {
  const lista = Array.isArray(padroes) ? padroes : [padroes];
  const uBs = lista.map(p => {
    const U = parseFloat(p?.incertezaExpandida);
    const k = parseFloat(p?.fatorK) || 2;
    return isNaN(U) || U <= 0 ? null : U / k;
  }).filter(v => v !== null);
  if (uBs.length === 0) return null;
  const uB = Math.sqrt(uBs.reduce((s, v) => s + v ** 2, 0));
  const uAval = (uA !== null && uA !== undefined) ? uA : 0;
  const uc = Math.sqrt(uAval ** 2 + uB ** 2);
  return { uB, uc, U: 2 * uc, kCal: 2 };
}

// ─── Helpers de EMP ───────────────────────────────────────────────────────────
// U: incerteza expandida a incluir na comparação (|erro| + U ≤ EMP)
function verificarEMPPorPonto(erro, aplicado, grupo, U = 0) {
  if (erro === null || erro === undefined || erro === '') return null;
  if (grupo.empTipo === 'text') return null;

  const empValor = parseFloat(grupo.empValor);
  if (isNaN(empValor)) return null;

  const erroMaisU = Math.abs(Number(erro)) + (Number(U) || 0);

  if (grupo.empTipo === 'maxError') {
    return erroMaisU <= empValor;
  }

  if (grupo.empTipo === 'percentage') {
    const aplicadoNum = parseFloat(aplicado);
    if (isNaN(aplicadoNum) || aplicadoNum === 0) return null;
    const limiteAbs = (empValor / 100) * Math.abs(aplicadoNum);
    return erroMaisU <= limiteAbs;
  }

  return null;
}

function rotuloEMP(grupo) {
  if (grupo.empTipo === 'text') return grupo.empValor;
  if (grupo.empTipo === 'percentage') return `±${grupo.empValor}%`;
  return `±${grupo.empValor} ${grupo.unidade}`;
}

// getU: função que retorna a incerteza U de um ponto (GUM interno ou manual externo)
function resumoGrupo(grupo, getU = () => 0) {
  let aprovados = 0, reprovados = 0, pendentes = 0;
  for (const p of grupo.pontos) {
    const resultado = verificarEMPPorPonto(p.erro, p.aplicado, grupo, getU(p));
    if (resultado === null) pendentes++;
    else if (resultado) aprovados++;
    else reprovados++;
  }
  return { aprovados, reprovados, pendentes, total: grupo.pontos.length };
}

function maiorErroGrupo(grupo, getU = () => 0) {
  if (grupo.empTipo !== 'maxError') return null;
  const empValor = parseFloat(grupo.empValor);
  if (isNaN(empValor)) return null;
  const pontos = grupo.pontos.filter(p => p.erro !== null && p.erro !== undefined && p.erro !== '');
  if (pontos.length === 0) return null;
  const max = Math.max(...pontos.map(p => Math.abs(Number(p.erro)) + (getU(p) || 0)));
  return { max, dentro: max <= empValor };
}

// ─── Micro-componentes para exibição dos dados da IA ─────────────────────────
function BadgeConfianca({ nivel }) {
  const cfg = COR_CONFIANCA[nivel] || COR_CONFIANCA.baixa;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${cfg.texto}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.rotulo}
    </span>
  );
}

function CampoIA({ rotulo, campo, unidade }) {
  if (!campo) return null;
  const temValor = campo.valor !== null && campo.valor !== undefined;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{rotulo}</div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-medium ${temValor ? 'text-slate-200' : 'text-slate-600 italic'}`}>
          {temValor ? `${campo.valor}${unidade ? ` ${unidade}` : ''}` : 'Não encontrado'}
        </span>
        {campo.confianca && <BadgeConfianca nivel={campo.confianca} />}
      </div>
    </div>
  );
}

// ─── Popup: vincular / cadastrar padrão de referência ─────────────────────────
function PadraoPickerPopup({ idx, padroesReferencia, laboratorios, onVincular, onFechar }) {
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroLocal, setErroLocal] = useState('');
  const [novoForm, setNovoForm] = useState({
    tag: '', descricao: '', tipo: 'trabalho', origem: 'externo',
    laboratorioId: '', numeroCertificado: '', nextCalibrationDate: '',
    incertezaExpandida: '', fatorK: '2',
  });

  const filtrados = padroesReferencia.filter(pr => {
    const q = busca.toLowerCase();
    return !q || pr.tag?.toLowerCase().includes(q) || pr.descricao?.toLowerCase().includes(q);
  });

  async function salvarNovo() {
    if (!novoForm.tag.trim() || !novoForm.descricao.trim()) {
      setErroLocal('Tag e descrição são obrigatórios.'); return;
    }
    setSalvando(true); setErroLocal('');
    try {
      const body = {
        tag: novoForm.tag.trim(),
        descricao: novoForm.descricao.trim(),
        tipo: novoForm.tipo,
        origem: novoForm.origem,
        laboratorioId: novoForm.laboratorioId || undefined,
        numeroCertificado: novoForm.numeroCertificado || undefined,
        nextCalibrationDate: novoForm.nextCalibrationDate || undefined,
        incertezaExpandida: novoForm.incertezaExpandida !== '' ? parseFloat(novoForm.incertezaExpandida) : undefined,
        fatorK: novoForm.fatorK !== '' ? parseFloat(novoForm.fatorK) : 2,
      };
      const { data } = await api.post('/padroesReferencia', body);
      onVincular(data);
    } catch (err) {
      setErroLocal(err.response?.data?.mensagem || 'Erro ao cadastrar padrão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onFechar}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative z-10 w-full max-w-lg bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl shadow-2xl flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--surface-4)]">
          <h3 className="text-sm font-semibold text-slate-100">Vincular Padrão de Rastreabilidade</h3>
          <button type="button" onClick={onFechar} className="text-slate-500 hover:text-slate-200 text-lg leading-none">×</button>
        </div>

        {!criando ? (
          <>
            {/* Busca */}
            <div className="px-5 py-3 border-b border-[var(--surface-4)]">
              <input
                autoFocus
                type="text"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por tag ou descrição..."
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {filtrados.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Nenhum padrão encontrado.</p>
              )}
              {filtrados.map(pr => (
                <button
                  key={pr.id || pr._id}
                  type="button"
                  onClick={() => onVincular(pr)}
                  className="w-full text-left bg-[var(--surface-3)] border border-[var(--surface-4)] hover:border-blue-600/60 rounded-lg px-4 py-3 transition-colors group"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono font-semibold text-blue-400">{pr.tag}</span>
                    <span className="text-[10px] text-slate-600 bg-[var(--surface-4)] px-1.5 py-0.5 rounded capitalize">{pr.tipo}</span>
                    <span className="text-[10px] text-slate-600 ml-auto">{pr.origem}</span>
                  </div>
                  <div className="text-sm text-slate-300 group-hover:text-slate-100 transition-colors">{pr.descricao}</div>
                  {pr.numeroCertificado && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Cert: {pr.numeroCertificado}
                      {pr.nextCalibrationDate && ` · Válido até: ${new Date(pr.nextCalibrationDate).toLocaleDateString('pt-BR')}`}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Rodapé */}
            <div className="px-5 py-3 border-t border-[var(--surface-4)]">
              <button
                type="button"
                onClick={() => setCriando(true)}
                className="w-full py-2 text-sm font-medium text-blue-400 hover:text-blue-300 border border-dashed border-blue-800/50 hover:border-blue-600 rounded-lg transition-colors"
              >
                + Cadastrar novo padrão
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Mini-form de cadastro */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {erroLocal && (
                <div className="bg-red-950/30 border border-red-800/50 rounded-lg px-3 py-2 text-xs text-red-300">{erroLocal}</div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Tag <span className="text-red-400">*</span></label>
                  <input type="text" value={novoForm.tag} onChange={e => setNovoForm(p => ({ ...p, tag: e.target.value }))}
                    placeholder="Ex: PAD-001"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Tipo</label>
                  <select value={novoForm.tipo} onChange={e => setNovoForm(p => ({ ...p, tipo: e.target.value }))}
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500">
                    {['primario','secundario','trabalho','transferencia'].map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Descrição <span className="text-red-400">*</span></label>
                <input type="text" value={novoForm.descricao} onChange={e => setNovoForm(p => ({ ...p, descricao: e.target.value }))}
                  placeholder="Ex: Manômetro padrão RBC classe 0,25%"
                  className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Origem</label>
                  <div className="flex rounded-lg overflow-hidden border border-[var(--surface-4)]">
                    {['interno','externo'].map(o => (
                      <button key={o} type="button" onClick={() => setNovoForm(p => ({ ...p, origem: o, laboratorioId: '' }))}
                        className={`flex-1 py-2 text-xs font-medium transition-colors ${novoForm.origem === o ? 'bg-blue-700 text-white' : 'bg-[var(--surface-3)] text-slate-400 hover:text-slate-200'}`}>
                        {o === 'interno' ? '🏭 Interno' : '🔬 Externo'}
                      </button>
                    ))}
                  </div>
                </div>
                {novoForm.origem === 'externo' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Laboratório</label>
                    <select value={novoForm.laboratorioId} onChange={e => setNovoForm(p => ({ ...p, laboratorioId: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500">
                      <option value="">— Nenhum —</option>
                      {laboratorios.map(lab => <option key={lab.id} value={lab.id}>{lab.nome}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nº Certificado</label>
                  <input type="text" value={novoForm.numeroCertificado} onChange={e => setNovoForm(p => ({ ...p, numeroCertificado: e.target.value }))}
                    placeholder="Ex: RBC-0312/2024"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Válido até</label>
                  <input type="date" value={novoForm.nextCalibrationDate} onChange={e => setNovoForm(p => ({ ...p, nextCalibrationDate: e.target.value }))}
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Incerteza Expandida U</label>
                  <input type="number" step="any" value={novoForm.incertezaExpandida} onChange={e => setNovoForm(p => ({ ...p, incertezaExpandida: e.target.value }))}
                    placeholder="Ex: 0.05"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Fator k</label>
                  <input type="number" step="0.1" value={novoForm.fatorK} onChange={e => setNovoForm(p => ({ ...p, fatorK: e.target.value }))}
                    placeholder="2"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>
              </div>
            </div>

            {/* Rodapé */}
            <div className="px-5 py-3 border-t border-[var(--surface-4)] flex gap-2">
              <button type="button" onClick={() => { setCriando(false); setErroLocal(''); }}
                className="flex-1 py-2 text-sm text-slate-400 border border-[var(--surface-4)] rounded-lg hover:text-slate-200 transition-colors">
                ← Voltar
              </button>
              <button type="button" onClick={salvarNovo} disabled={salvando}
                className="flex-1 py-2 text-sm font-medium bg-blue-700 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50">
                {salvando ? 'Salvando...' : 'Cadastrar e vincular'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CalibrationForm({ calibracaoExistente, onSucesso, onCancelar, onRascunhoSalvo }) {
  const { tenant } = useTenant();
  const somenteExterna = !tenant || tenant.planoCalibracao === 'external_only';
  const [form, setForm] = useState(estadoInicial);
  const [instrumentos, setInstrumentos] = useState([]);
  const [laboratorios, setLaboratorios] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [arquivoCertificado, setArquivoCertificado] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [padroesReferencia, setPadroesReferencia] = useState([]);
  const [padraoPickerIdx, setPadraoPickerIdx] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [camposFaltando, setCamposFaltando] = useState([]);
  const [carregandoInstrumentos, setCarregandoInstrumentos] = useState(true);
  const [rascunhoId, setRascunhoId] = useState(calibracaoExistente?._id || null);
  const [ultimoAutoSave, setUltimoAutoSave] = useState(null);
  const [autoSalvando, setAutoSalvando] = useState(false);
  const autoSalvarFnRef = useRef(null);
  const [criterioAprovacaoClasse, setCriterioAprovacaoClasse] = useState('herdar');

  // ─── Estado do painel IA (direito) ────────────────────────────────────────
  const [estadoScan, setEstadoScan] = useState(null); // null | 'escaneando' | 'sucesso' | 'erro'
  const [dadosIA, setDadosIA] = useState(null);
  const [erroScan, setErroScan] = useState('');

  const ehEdicao = Boolean(calibracaoExistente?._id);
  const ehInterna = form.calibrationType === 'interna';
  const isImagem = arquivoCertificado?.type?.startsWith('image/');
  // critério efetivo: classe → tenant → conservador (padrão ILAC G8)
  const criterioEfetivo = criterioAprovacaoClasse === 'herdar'
    ? (tenant?.criterioAprovacao || 'conservador')
    : criterioAprovacaoClasse;

  // ─── Preview URL do arquivo selecionado ──────────────────────────────────
  useEffect(() => {
    if (!arquivoCertificado) { setPreviewUrl(''); return; }
    const url = URL.createObjectURL(arquivoCertificado);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivoCertificado]);

  // ─── Auto-save rascunho a cada 5 min ─────────────────────────────────────
  // Ref sempre aponta para a versão mais recente da função (evita closure stale)
  useEffect(() => {
    autoSalvarFnRef.current = async function autoSalvar() {
      if (!form.instrumentId || !form.data || carregando || autoSalvando) return;
      setAutoSalvando(true);
      try {
        const payload = new FormData();
        payload.append('instrumentId', form.instrumentId);
        payload.append('calibrationType', form.calibrationType);
        payload.append('data', form.data);
        payload.append('statusLaudo', 'rascunho');
        if (form.proximaData) payload.append('proximaData', form.proximaData);
        if (form.resultado) payload.append('resultado', form.resultado);
        if (form.observacoes) payload.append('observacoes', form.observacoes);
        if (form.procedimentoId) payload.append('procedimentoId', form.procedimentoId);
        if (!ehInterna && form.fornecedor) payload.append('fornecedor', form.fornecedor);
        if (form.gruposMedicao.length > 0) {
          const gruposParaEnviar = form.gruposMedicao.map(g => {
            const padraoIdGrupo = g.padraoReferenciaId || null;
            return {
              ...g,
              padraoReferenciaId: padraoIdGrupo,
              pontos: g.pontos.map(p => {
                const leiturasValidas = (p.leituras || []).filter(v => v !== '').map(Number).filter(v => !isNaN(v));
                const padraoIdPonto = (g.usarPadraoPorPonto && p.padraoReferenciaId) ? p.padraoReferenciaId : padraoIdGrupo;
                const padrao = padraoIdPonto ? padroesReferencia.find(x => (x.id || x._id) === padraoIdPonto) : null;
                const gum = ehInterna ? calcularGUM(p.uA, padrao ? [padrao] : form.padroesUsados) : null;
                return { ...p, padraoReferenciaId: p.padraoReferenciaId || null, incertezaTipoA: p.uA ?? null, incertezaTipoB: gum?.uB ?? null, fatorK: gum?.kCal ?? null, incerteza: gum?.U ?? (p.incerteza || null), medicoesRepetidas: leiturasValidas.length > 1 ? leiturasValidas : [] };
              }),
            };
          });
          payload.append('gruposMedicao', JSON.stringify(gruposParaEnviar));
        }
        payload.append('padroesUsados', JSON.stringify(form.padroesUsados.map(p => ({
          padraoReferenciaId: p.padraoReferenciaId || null, descricao: p.descricao,
          numeroCertificado: p.numeroCertificado, validoAte: p.validoAte,
          incertezaExpandida: p.incertezaExpandida !== '' ? parseFloat(p.incertezaExpandida) : null,
          fatorK: p.fatorK !== '' ? parseFloat(p.fatorK) : 2,
        }))));
        const headers = { 'Content-Type': 'multipart/form-data' };
        const idAtual = rascunhoId;
        if (idAtual) {
          await api.put(`/calibrations/${idAtual}`, payload, { headers });
        } else {
          const { data } = await api.post('/calibrations', payload, { headers });
          setRascunhoId(data._id || data.id);
        }
        setUltimoAutoSave(new Date());
      } catch { /* falha silenciosa no auto-save */ }
      setAutoSalvando(false);
    };
  });

  useEffect(() => {
    if (ehEdicao) return;
    const timer = setInterval(() => autoSalvarFnRef.current?.(), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [ehEdicao]);

  // ─── Resumo geral de EMP (todos os grupos) ─────────────────────────────────
  const resumoGeral = useMemo(() => {
    if (form.gruposMedicao.length === 0) return null;
    const interna = form.calibrationType === 'interna';
    let totalAprovados = 0, totalReprovados = 0, totalPendentes = 0, total = 0;
    for (const grupo of form.gruposMedicao) {
      const getU = interna ? makeGetU(grupo) : (p => parseFloat(p.incerteza) || 0); // eslint-disable-line no-use-before-define
      const r = resumoGrupo(grupo, getU);
      totalAprovados += r.aprovados;
      totalReprovados += r.reprovados;
      totalPendentes += r.pendentes;
      total += r.total;
    }
    return { aprovados: totalAprovados, reprovados: totalReprovados, pendentes: totalPendentes, total };
  }, [form.gruposMedicao, form.padroesUsados, form.calibrationType, padroesReferencia, criterioEfetivo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sugestão automática de resultado baseada no EMP ──────────────────────
  const sugestaoResultado = useMemo(() => {
    if (!resumoGeral || resumoGeral.total === 0) return null;
    if (resumoGeral.pendentes > 0) return null;
    if (resumoGeral.reprovados === 0) return 'aprovado';
    if (resumoGeral.reprovados < resumoGeral.total) return 'aprovado_com_restricao';
    return 'reprovado';
  }, [resumoGeral]);

  // ─── Carrega instrumentos + catálogos ────────────────────────────────────
  useEffect(() => {
    async function buscarInstrumentos() {
      try {
        const key = '/instruments?calibratable=true';
        const cached = cacheGet(key);
        if (cached) { setInstrumentos(cached); setCarregandoInstrumentos(false); return; }
        const { data } = await api.get(`${key}&limit=200`);
        const list = data.dados || data;
        cacheSet(key, list, 5 * 60_000);
        setInstrumentos(list);
      } catch {
        setErro('Não foi possível carregar a lista de instrumentos.');
      } finally {
        setCarregandoInstrumentos(false);
      }
    }

    function fetchCached(url, setter, ttl = 5 * 60_000) {
      const cached = cacheGet(url);
      if (cached) { setter(cached); return; }
      api.get(url).then((r) => { cacheSet(url, r.data, ttl); setter(r.data || []); }).catch(() => {});
    }

    buscarInstrumentos();
    fetchCached('/laboratorios', setLaboratorios);
    fetchCached('/procedimentos', setProcedimentos);
    // Padrões: externos (PadraoReferencia) + internos (Instruments classificacao=padrao)
    Promise.all([
      api.get('/padroesReferencia').catch(() => ({ data: [] })),
      api.get('/instruments?classificacao=padrao&limit=200').catch(() => ({ data: { dados: [] } })),
    ]).then(([extRes, intRes]) => {
      const externos = extRes.data || [];
      const internos = (intRes.data.dados || intRes.data || []).map(i => ({
        id: i._id || i.id, _id: i._id || i.id,
        tag: i.tag, descricao: i.description,
        tipo: 'padrao_interno', origem: 'interno',
        numeroCertificado: i.padraoReferencia?.numeroCertificado || '',
        nextCalibrationDate: i.nextCalibrationDate,
        incertezaExpandida: i.padraoReferencia?.incertezaExpandida,
        fatorK: i.padraoReferencia?.fatorK ?? 2,
        _instrumentId: i._id || i.id,
      }));
      // Mescla: internos vindos de Instruments (não duplicar com PadraoReferencia vinculado)
      const idsInternos = new Set(internos.map(i => i._instrumentId));
      const externosFiltrados = externos.filter(e => !e.instrumentId || !idsInternos.has(e.instrumentId));
      const merged = [...internos, ...externosFiltrados];
      setPadroesReferencia(merged);
    });
  }, []);

  // ─── Preenche formulário em modo edição ───────────────────────────────────
  useEffect(() => {
    if (calibracaoExistente) {
      setForm({
        instrumentId: calibracaoExistente.instrumentId?._id || calibracaoExistente.instrumentId || '',
        calibrationType: calibracaoExistente.calibrationType || 'externa',
        data: formatarDataInput(calibracaoExistente.data),
        proximaData: formatarDataInput(calibracaoExistente.proximaData),
        resultado: calibracaoExistente.resultado || '',
        fornecedor: calibracaoExistente.fornecedor || '',
        acreditacaoFornecedor: calibracaoExistente.acreditacaoFornecedor || '',
        numeroCertificado: calibracaoExistente.numeroCertificado || '',
        observacoes: calibracaoExistente.observacoes || '',
        statusLaudo: calibracaoExistente.statusLaudo || 'rascunho',
        procedimentoId: calibracaoExistente.procedimentoId || calibracaoExistente.procedimento?.id || '',
        gruposMedicao: (calibracaoExistente.gruposMedicao || []).map(g => {
          const nLeituras = Math.max(
            ...(g.pontos || []).map(p => p.medicoesRepetidas?.length || 1),
            1
          );
          const pontosRestaurados = (g.pontos || []).map(p => ({
            ...p,
            padraoReferenciaId: p.padraoReferenciaId || null,
            leituras: (p.medicoesRepetidas || []).map(m =>
              String(typeof m === 'object' ? m.valor : m)
            ),
            uA: p.incertezaTipoA ?? null,
          }));
          // Detecta se os pontos usam padrões diferentes entre si
          const idsNaoNulos = pontosRestaurados.map(p => p.padraoReferenciaId).filter(Boolean);
          const unicos = [...new Set(idsNaoNulos)];
          const usarPorPonto = unicos.length > 1;
          return {
            ...g,
            padraoReferenciaId: g.padraoReferenciaId || null,
            usarPadraoPorPonto: usarPorPonto,
            numLeituras: nLeituras,
            pontos: pontosRestaurados,
          };
        }),
        padroesUsados: calibracaoExistente.padroesUsados?.length > 0
          ? calibracaoExistente.padroesUsados.map(p => ({
              padraoReferenciaId: p.padraoReferenciaId || null,
              descricao: p.descricao || '',
              numeroCertificado: p.numeroCertificado || '',
              validoAte: p.validoAte ? p.validoAte.split('T')[0] : '',
              incertezaExpandida: p.incertezaExpandida != null ? String(p.incertezaExpandida) : '',
              fatorK: p.fatorK != null ? String(p.fatorK) : '2',
            }))
          : [padraoVazio()],
      });
    }
  }, [calibracaoExistente]);

  // ─── Quando instrumento muda, busca detalhe completo e herda grandezas ──────
  useEffect(() => {
    if (!form.instrumentId || ehEdicao || form.gruposMedicao.length > 0) return;
    api.get(`/instruments/${form.instrumentId}`).then(({ data: inst }) => {
      if (!inst?.quantities?.length) return;
      setCriterioAprovacaoClasse(inst.instrumentClass?.criterioAprovacao || 'herdar');
      const criterioClasse = inst.instrumentClass?.criterioAceitacao || null;
      const gruposHerdados = inst.quantities.map((q) => {
        const unidade = q.unit || '—';
        let empTipo = q.acceptanceCriteria?.type || 'maxError';
        let empValor;
        if (q.acceptanceCriteria?.maxError != null) {
          empValor = String(q.acceptanceCriteria.maxError);
        } else if (q.acceptanceCriteria?.value) {
          empValor = q.acceptanceCriteria.value;
        } else {
          empValor = 'N/D';
        }
        if (empValor === 'N/D' && criterioClasse) {
          const parsed = parseCriterioEmp(criterioClasse);
          if (parsed) { empTipo = parsed.empTipo; empValor = parsed.empValor; }
        }
        const numLeituras = q.numLeituras || 1;
        const estrategia = q.estrategiaVarredura || 'asc';
        const pontosBase = (q.calibrationPoints || []).map((p) => ({
          aplicado:          p.applied,
          lido:              '',
          erro:              null,
          incerteza:         '',
          aprovado:          null,
          leituras:          [],
          uA:                null,
          padraoReferenciaId: null,
        }));
        const pontosOrdenados = (() => {
          const asc = [...pontosBase].sort((a, b) => a.aplicado - b.aplicado);
          const desc = [...asc].reverse();
          if (estrategia === 'desc') return desc;
          if (estrategia === 'asc_desc') return [
            ...asc.map(p => ({ ...p, _dir: '↑' })),
            ...desc.map(p => ({ ...p, _dir: '↓' })),
          ];
          if (estrategia === 'desc_asc') return [
            ...desc.map(p => ({ ...p, _dir: '↓' })),
            ...asc.map(p => ({ ...p, _dir: '↑' })),
          ];
          return asc;
        })();
        return {
          nome:               q.name,
          unidade,
          empTipo,
          empValor,
          numLeituras,
          estrategiaVarredura: estrategia,
          padraoReferenciaId:   null,
          usarPadraoPorPonto:   false,
          pontos:              pontosOrdenados,
        };
      });
      setForm((prev) => ({ ...prev, gruposMedicao: gruposHerdados }));
    }).catch(() => {});
  }, [form.instrumentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Ao trocar para interna, limpa campos exclusivos de externa ───────────
  useEffect(() => {
    if (ehInterna) {
      setForm((prev) => ({ ...prev, fornecedor: '', acreditacaoFornecedor: '' }));
    }
  }, [ehInterna]);

  // ─── Sugestão de proximaData com base na frequência (chip, não auto-fill) ──
  const proximaDataSugerida = (() => {
    if (!form.data || !form.instrumentId) return null;
    const inst = instrumentos.find((i) => i._id === form.instrumentId);
    const freq = inst?.calibrationFrequencyDays ?? inst?.instrumentClass?.calibrationFrequencyDays;
    if (!freq) return null;
    const base = new Date(form.data);
    if (isNaN(base)) return null;
    base.setDate(base.getDate() + freq);
    return { data: base.toISOString().split('T')[0], freq, label: base.toLocaleDateString('pt-BR') };
  })();

  // ─── Handlers genéricos ───────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value } = e.target;
    if (camposFaltando.length > 0) setCamposFaltando([]);
    if (name === 'instrumentId') {
      setForm((prev) => ({ ...prev, instrumentId: value, gruposMedicao: [] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }

  function updatePadrao(idx, field, value) {
    setForm((prev) => {
      const padroesUsados = prev.padroesUsados.map((p, i) => i === idx ? { ...p, [field]: value } : p);
      return { ...prev, padroesUsados };
    });
  }

  function addPadrao() {
    setForm((prev) => ({ ...prev, padroesUsados: [...prev.padroesUsados, padraoVazio()] }));
  }

  function removePadrao(idx) {
    setForm((prev) => ({ ...prev, padroesUsados: prev.padroesUsados.filter((_, i) => i !== idx) }));
  }

  function vincularPadrao(idx, pr) {
    setForm((prev) => {
      const padroesUsados = prev.padroesUsados.map((p, i) => i !== idx ? p : {
        padraoReferenciaId: pr.id || pr._id || null,
        descricao: pr.descricao || p.descricao,
        numeroCertificado: pr.numeroCertificado || p.numeroCertificado,
        validoAte: pr.nextCalibrationDate ? pr.nextCalibrationDate.split('T')[0] : p.validoAte,
        incertezaExpandida: pr.incertezaExpandida != null ? String(pr.incertezaExpandida) : p.incertezaExpandida,
        fatorK: pr.fatorK != null ? String(pr.fatorK) : p.fatorK,
      });
      return { ...prev, padroesUsados };
    });
    if (pr.id || pr._id) {
      setPadroesReferencia(prev => prev.some(x => (x.id || x._id) === (pr.id || pr._id)) ? prev : [...prev, pr]);
    }
    setPadraoPickerIdx(null);
  }

  // ─── Toggle padrão por ponto dentro de um grupo ──────────────────────────────
  function togglePadraoPorPonto(gi) {
    setForm(prev => ({
      ...prev,
      gruposMedicao: prev.gruposMedicao.map((g, i) =>
        i === gi ? { ...g, usarPadraoPorPonto: !g.usarPadraoPorPonto } : g
      ),
    }));
  }

  // ─── Vincular padrão a um ponto específico ───────────────────────────────────
  function vincularPadraoPonto(gi, pi, pr) {
    const id = pr.id || pr._id || null;
    setForm(prev => ({
      ...prev,
      gruposMedicao: prev.gruposMedicao.map((g, i) => {
        if (i !== gi) return g;
        return {
          ...g,
          pontos: g.pontos.map((p, j) =>
            j === pi ? { ...p, padraoReferenciaId: id } : p
          ),
        };
      }),
    }));
  }

  // ─── Vincular padrão a um grupo específico ───────────────────────────────────
  function vincularPadraoGrupo(gi, pr) {
    const id = pr.id || pr._id || null;
    setForm(prev => ({
      ...prev,
      gruposMedicao: prev.gruposMedicao.map((g, i) =>
        i === gi ? { ...g, padraoReferenciaId: id } : g
      ),
    }));
  }

  // ─── makeGetU: factory que retorna getU(ponto) para um grupo específico ─────
  // Resolve padrão: global (checkbox ativado) ou por grupo → GUM com esse padrão.
  // Critério simples: U=0. Externo: U manual do campo incerteza.
  function makeGetU(grupo) {
    return function getU(ponto) {
      if (criterioEfetivo === 'simples') return 0;
      if (ehInterna) {
        // Por ponto (quando toggle ativo) > padrão do grupo
        const padraoId = (grupo?.usarPadraoPorPonto && ponto?.padraoReferenciaId)
          ? ponto.padraoReferenciaId
          : (grupo?.padraoReferenciaId || null);
        const padrao = padraoId ? padroesReferencia.find(x => (x.id || x._id) === padraoId) : null;
        const gum = calcularGUM(ponto.uA, padrao ? [padrao] : form.padroesUsados);
        return gum?.U ?? 0;
      }
      return parseFloat(ponto.incerteza) || 0;
    };
  }

  // ─── Handler de ponto com cálculo de erro em tempo real ──────────────────
  function handlePontoChange(grupoIdx, pontoIdx, campo, valor) {
    setForm((prev) => {
      const grupos = prev.gruposMedicao.map((g, gi) => {
        if (gi !== grupoIdx) return g;
        const pontos = g.pontos.map((p, pi) => {
          if (pi !== pontoIdx) return p;
          const atualizado = { ...p, [campo]: valor === '' ? '' : valor };
          if (campo === 'lido' || campo === 'aplicado') {
            const lido = campo === 'lido' ? valor : p.lido;
            const aplicado = campo === 'aplicado' ? valor : p.aplicado;
            if (lido !== '' && aplicado !== '' && !isNaN(Number(lido)) && !isNaN(Number(aplicado))) {
              atualizado.erro = parseFloat((Number(lido) - Number(aplicado)).toFixed(6));
            } else {
              atualizado.erro = null;
            }
          }
          return atualizado;
        });
        return { ...g, pontos };
      });
      return { ...prev, gruposMedicao: grupos };
    });
  }

  // ─── Adicionar / remover pontos manualmente ──────────────────────────────
  function adicionarPonto(grupoIdx) {
    setForm((prev) => {
      const grupos = prev.gruposMedicao.map((g, gi) => {
        if (gi !== grupoIdx) return g;
        const n = g.numLeituras || 1;
        return { ...g, pontos: [...g.pontos, { aplicado: '', lido: '', erro: null, incerteza: '', leituras: n > 1 ? Array(n).fill('') : [], uA: null, padraoReferenciaId: null }] };
      });
      return { ...prev, gruposMedicao: grupos };
    });
  }

  function calcularEstatisticasLeituras(leituras) {
    const vals = (leituras || []).filter(v => v !== '').map(Number).filter(v => !isNaN(v));
    if (vals.length === 0) return { media: null, desvio: null, uA: null };
    if (vals.length === 1) return { media: vals[0], desvio: null, uA: null };
    const n = vals.length;
    const media = vals.reduce((s, v) => s + v, 0) / n;
    const variancia = vals.reduce((s, v) => s + Math.pow(v - media, 2), 0) / (n - 1);
    const desvio = Math.sqrt(variancia);
    return { media, desvio, uA: desvio / Math.sqrt(n) };
  }

  function handleNumLeiturasChange(grupoIdx, novoN) {
    const n = parseInt(novoN) || 1;
    setForm((prev) => {
      const grupos = prev.gruposMedicao.map((g, gi) => {
        if (gi !== grupoIdx) return g;
        const pontos = g.pontos.map(p => {
          if (n === 1) return { ...p, leituras: [], uA: null };
          const leituras = Array.from({ length: n }, (_, i) => (p.leituras || [])[i] ?? '');
          const { media, uA } = calcularEstatisticasLeituras(leituras);
          const lido = media !== null ? String(media) : '';
          const aplicadoNum = parseFloat(p.aplicado);
          const erro = lido !== '' && !isNaN(aplicadoNum) ? parseFloat(lido) - aplicadoNum : null;
          return { ...p, leituras, lido, erro, uA: uA ?? null };
        });
        return { ...g, numLeituras: n, pontos };
      });
      return { ...prev, gruposMedicao: grupos };
    });
  }

  function handleLeituraChange(grupoIdx, pontoIdx, leituraIdx, valor) {
    setForm((prev) => {
      const grupos = prev.gruposMedicao.map((g, gi) => {
        if (gi !== grupoIdx) return g;
        const pontos = g.pontos.map((p, pi) => {
          if (pi !== pontoIdx) return p;
          const leituras = [...(p.leituras || [])];
          leituras[leituraIdx] = valor;
          const { media, uA } = calcularEstatisticasLeituras(leituras);
          const lido = media !== null ? String(media) : '';
          const aplicadoNum = parseFloat(p.aplicado);
          const erro = lido !== '' && !isNaN(aplicadoNum) ? parseFloat(lido) - aplicadoNum : null;
          return { ...p, leituras, lido, erro, uA: uA ?? null };
        });
        return { ...g, pontos };
      });
      return { ...prev, gruposMedicao: grupos };
    });
  }

  function removerPonto(grupoIdx, pontoIdx) {
    setForm((prev) => {
      const grupos = prev.gruposMedicao.map((g, gi) => {
        if (gi !== grupoIdx) return g;
        return { ...g, pontos: g.pontos.filter((_, pi) => pi !== pontoIdx) };
      });
      return { ...prev, gruposMedicao: grupos };
    });
  }

  // ─── Scan com IA ─────────────────────────────────────────────────────────
  async function iniciarScan() {
    setEstadoScan('escaneando');
    setDadosIA(null);
    setErroScan('');
    try {
      const formData = new FormData();
      formData.append('certificate', arquivoCertificado);
      const { data } = await api.post('/calibrations/scan-certificate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDadosIA(data);
      setEstadoScan('sucesso');
    } catch (err) {
      setErroScan(err.response?.data?.mensagem || 'Erro ao analisar o certificado. Tente novamente.');
      setEstadoScan('erro');
    }
  }

  // ─── Aplica dados extraídos pela IA ──────────────────────────────────────
  async function handleAplicarDadosIA(dados) {
    // Busca instrumento completo (com quantities + EMP) quando IA detecta a TAG
    let instCompleto = null;
    let instIdParaAplicar = null;

    if (dados.tagEquipamento?.valor && !form.instrumentId) {
      const tag = dados.tagEquipamento.valor.toString().trim().toLowerCase();
      const inst = instrumentos.find((i) => (i.tag || '').toLowerCase() === tag);
      if (inst) {
        instIdParaAplicar = inst._id;
        try {
          const { data } = await api.get(`/instruments/${inst._id}`);
          instCompleto = data;
        } catch {}
      }
    }

    setForm((prev) => {
      const novo = { ...prev };

      if (dados.numeroCertificado?.valor) novo.numeroCertificado = dados.numeroCertificado.valor;
      if (dados.dataCalibracao?.valor)    novo.data         = dados.dataCalibracao.valor;
      if (dados.proximaData?.valor)       novo.proximaData  = dados.proximaData.valor;
      if (dados.resultado?.valor)         novo.resultado    = dados.resultado.valor;
      if (dados.fornecedor?.valor)        novo.fornecedor   = dados.fornecedor.valor;

      if (instIdParaAplicar) novo.instrumentId = instIdParaAplicar;

      if (dados.padraoRastreabilidade) {
        const p = dados.padraoRastreabilidade;
        const primeiro = prev.padroesUsados[0] || padraoVazio();
        novo.padroesUsados = [
          {
            ...primeiro,
            descricao:         p.descricao?.valor         || primeiro.descricao,
            numeroCertificado: p.numeroCertificado?.valor || primeiro.numeroCertificado,
            validoAte:         p.validoAte?.valor         || primeiro.validoAte,
          },
          ...prev.padroesUsados.slice(1),
        ];
      }

      if (dados.gruposMedicao?.length > 0) {
        if (prev.gruposMedicao.length > 0) {
          // Instrumento já selecionado antes da IA: mescla pontos mantendo EMP existente
          novo.gruposMedicao = prev.gruposMedicao.map((grupo, gi) => {
            const grupoIA = dados.gruposMedicao.find(
              (g) => g.nome.toLowerCase() === grupo.nome.toLowerCase()
            ) ?? dados.gruposMedicao[gi];
            if (!grupoIA || !grupoIA.pontos?.length) return grupo;

            if (grupo.pontos.length > 0) {
              return {
                ...grupo,
                pontos: grupo.pontos.map((ponto, pi) => {
                  const pontoIA = grupoIA.pontos[pi];
                  if (!pontoIA) return ponto;
                  return {
                    ...ponto,
                    lido:      numSeguro(pontoIA.lido,      ponto.lido),
                    erro:      typeof pontoIA.erro === 'number' ? pontoIA.erro : ponto.erro,
                    incerteza: numSeguro(pontoIA.incerteza, ponto.incerteza),
                  };
                }),
              };
            }
            return {
              ...grupo,
              pontos: grupoIA.pontos.map((p) => ({
                aplicado:  numSeguro(p.aplicado,  0),
                lido:      numSeguro(p.lido,      0),
                erro:      typeof p.erro === 'number' ? p.erro : null,
                incerteza: p.incerteza != null ? numSeguro(p.incerteza, null) : null,
                aprovado:  null,
              })),
            };
          });
        } else if (instCompleto?.quantities?.length > 0) {
          // IA encontrou instrumento: constrói grupos com EMP do instrumento + pontos da IA
          const criterioClasse = instCompleto.instrumentClass?.criterioAceitacao || null;
          novo.gruposMedicao = instCompleto.quantities.map((q, qi) => {
            const unidade = q.unit || '—';
            let empTipo = q.acceptanceCriteria?.type || 'maxError';
            let empValor;
            if (q.acceptanceCriteria?.maxError != null) {
              empValor = String(q.acceptanceCriteria.maxError);
            } else if (q.acceptanceCriteria?.value) {
              empValor = q.acceptanceCriteria.value;
            } else {
              empValor = 'N/D';
            }
            if (empValor === 'N/D' && criterioClasse) {
              const parsed = parseCriterioEmp(criterioClasse);
              if (parsed) { empTipo = parsed.empTipo; empValor = parsed.empValor; }
            }

            const grupoIA = dados.gruposMedicao.find(
              (g) => g.nome.toLowerCase() === q.name.toLowerCase()
            ) ?? dados.gruposMedicao[qi];

            const pontos = grupoIA?.pontos?.length > 0
              ? grupoIA.pontos.map((p) => ({
                  aplicado:  numSeguro(p.aplicado,  0),
                  lido:      numSeguro(p.lido,      0),
                  erro:      typeof p.erro === 'number' ? p.erro : null,
                  incerteza: p.incerteza != null ? numSeguro(p.incerteza, null) : null,
                  aprovado:  null,
                }))
              : (q.calibrationPoints || []).map((p) => ({
                  aplicado:  p.applied,
                  lido:      '',
                  erro:      null,
                  incerteza: '',
                  aprovado:  null,
                }));

            return { nome: q.name, unidade, empTipo, empValor, pontos };
          });
        } else {
          // Instrumento não encontrado ou sem grandezas: usa dados da IA sem EMP
          novo.gruposMedicao = dados.gruposMedicao.map((g) => ({
            nome:     g.nome,
            unidade:  g.unidade || '—',
            empTipo:  'text',
            empValor: 'Definir EMP',
            pontos:   g.pontos.map((p) => ({
              aplicado:  numSeguro(p.aplicado,  0),
              lido:      numSeguro(p.lido,      0),
              erro:      typeof p.erro === 'number' ? p.erro : null,
              incerteza: p.incerteza != null ? numSeguro(p.incerteza, null) : null,
              aprovado:  null,
            })),
          }));
        }
      }

      return novo;
    });
    setEstadoScan(null);
  }

  // ─── Submissão ────────────────────────────────────────────────────────────
  async function handleSubmit(e, modoRascunho = false) {
    if (e && e.preventDefault) e.preventDefault();
    setErro('');
    setCamposFaltando([]);

    // Rascunho: só exige instrumento e data
    if (!form.instrumentId) return setErro('Selecione um instrumento.');
    if (!form.data) return setErro('Informe a data da calibração.');

    if (!modoRascunho) {
      const faltando = [];
      if (!form.proximaData) faltando.push({ id: 'campo-proxima-data', rotulo: 'Próxima data de calibração' });
      if (!form.resultado) faltando.push({ id: 'campo-resultado', rotulo: 'Resultado' });
      if (!ehInterna && !form.fornecedor.trim()) faltando.push({ id: 'campo-fornecedor', rotulo: 'Fornecedor / Laboratório' });
      if (!form.padroesUsados[0]?.descricao.trim() && !form.padroesUsados[0]?.padraoReferenciaId) {
        faltando.push({ id: 'campo-padroes', rotulo: 'Padrão de rastreabilidade' });
      } else {
        const dataRef = form.data ? new Date(form.data) : new Date();
        dataRef.setHours(0, 0, 0, 0);
        for (const p of form.padroesUsados) {
          if (!ehInterna && (!p.numeroCertificado.trim() || !p.validoAte)) {
            faltando.push({ id: 'campo-padroes', rotulo: 'Número de certificado e validade do padrão' });
            break;
          }
          if (p.validoAte && new Date(p.validoAte) < dataRef) {
            faltando.push({ id: 'campo-padroes', rotulo: `Padrão vencido na data da calibração (${new Date(p.validoAte).toLocaleDateString('pt-BR')})` });
            break;
          }
        }
      }
      if (faltando.length > 0) {
        setCamposFaltando(faltando);
        return;
      }
    }

    setCarregando(true);

    try {
      const payload = new FormData();
      const campos = [
        'instrumentId', 'calibrationType', 'data', 'proximaData',
        'resultado', 'observacoes', 'numeroCertificado',
        'procedimentoId',
      ];
      campos.forEach((c) => { if (form[c]) payload.append(c, form[c]); });
      const statusFinal = modoRascunho ? 'rascunho'
        : (tenant?.modoAprovacao === 'simplificado' ? 'concluido' : 'em_revisao');
      payload.append('statusLaudo', statusFinal);

      if (!ehInterna) {
        if (form.fornecedor) payload.append('fornecedor', form.fornecedor);
        if (form.acreditacaoFornecedor) payload.append('acreditacaoFornecedor', form.acreditacaoFornecedor);
      }

      if (form.gruposMedicao.length > 0) {
        const gruposParaEnviar = form.gruposMedicao.map(g => {
          const padraoIdGrupo = g.padraoReferenciaId || null;
          return {
            ...g,
            padraoReferenciaId: padraoIdGrupo,
            pontos: (g.pontos || []).map(p => {
              const leiturasValidas = (p.leituras || [])
                .filter(v => v !== '')
                .map(Number)
                .filter(v => !isNaN(v));
              const padraoIdPonto = (g.usarPadraoPorPonto && p.padraoReferenciaId) ? p.padraoReferenciaId : padraoIdGrupo;
              const padrao = padraoIdPonto ? padroesReferencia.find(x => (x.id || x._id) === padraoIdPonto) : null;
              const gum = ehInterna ? calcularGUM(p.uA, padrao ? [padrao] : form.padroesUsados) : null;
              return {
                ...p,
                padraoReferenciaId: p.padraoReferenciaId || null,
                incertezaTipoA: p.uA ?? p.incertezaTipoA ?? null,
                incertezaTipoB: gum ? gum.uB : (p.incertezaTipoB ?? null),
                fatorK: gum ? gum.kCal : (p.fatorK !== '' ? parseFloat(p.fatorK) : null),
                incerteza: gum ? gum.U : (p.incerteza !== '' ? p.incerteza : null),
                medicoesRepetidas: leiturasValidas.length > 1 ? leiturasValidas : [],
              };
            }),
          };
        });
        payload.append('gruposMedicao', JSON.stringify(gruposParaEnviar));
      }

      payload.append('padroesUsados', JSON.stringify(form.padroesUsados.map(p => ({
        padraoReferenciaId: p.padraoReferenciaId || null,
        descricao: p.descricao,
        numeroCertificado: p.numeroCertificado,
        validoAte: p.validoAte,
        incertezaExpandida: p.incertezaExpandida !== '' ? parseFloat(p.incertezaExpandida) : null,
        fatorK: p.fatorK !== '' ? parseFloat(p.fatorK) : 2,
      }))));

      if (arquivoCertificado) {
        payload.append('arquivoCertificado', arquivoCertificado);
      }

      if (ehEdicao) {
        await api.put(`/calibrations/${calibracaoExistente._id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (rascunhoId) {
        await api.put(`/calibrations/${rascunhoId}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const { data: criado } = await api.post('/calibrations', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // Captura ID para PUT subsequentes (auto-save ou novo clique em Salvar Rascunho)
        if (criado?._id || criado?.id) setRascunhoId(criado._id || criado.id);
      }

      if (modoRascunho) {
        setUltimoAutoSave(new Date());
        setErro('');
        onRascunhoSalvo?.();
        return;
      }
      onSucesso?.();
    } catch (err) {
      const msg = err.response?.data?.mensagem || 'Erro ao salvar calibração. Tente novamente.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  }

  // ─── Render do painel direito (PDF ou IA) ─────────────────────────────────
  function renderPainelDireito() {
    const p = dadosIA?.padraoRastreabilidade || {};
    const grupos = dadosIA?.gruposMedicao || [];

    if (estadoScan === 'escaneando') {
      return (
        <div className="flex-1 p-6 space-y-3">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-indigo-300 font-medium">Analisando com IA...</span>
          </div>
          {[80, 60, 70, 50, 65].map((w, i) => (
            <div key={i} className="h-3 bg-[var(--surface-3)] rounded animate-pulse" style={{ width: `${w}%` }} />
          ))}
        </div>
      );
    }

    if (estadoScan === 'erro') {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 p-6">
            <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-4">
              <p className="text-sm font-medium text-red-300">Falha na análise</p>
              <p className="text-xs text-red-400/80 mt-1">{erroScan}</p>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-[var(--surface-4)] bg-[var(--surface-2)] flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setEstadoScan(null)}
              className="px-3 py-1.5 text-xs text-slate-400 border border-[var(--surface-4)] rounded-lg hover:text-slate-200 transition-colors"
            >
              ← Voltar ao PDF
            </button>
            <button
              type="button"
              onClick={iniciarScan}
              className="px-3 py-1.5 text-xs font-medium bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    if (estadoScan === 'sucesso' && dadosIA) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--surface-4)] bg-[var(--surface-2)]">
            <div className="flex items-center gap-2">
              <span className="text-indigo-300">🤖</span>
              <span className="text-sm font-semibold text-indigo-200">Dados extraídos pela IA</span>
              <span className="text-[10px] text-indigo-500 bg-indigo-900/40 px-2 py-0.5 rounded-full border border-indigo-800/40">
                Gemini Vision
              </span>
            </div>
            <button
              type="button"
              onClick={() => setEstadoScan(null)}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              ← PDF
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="flex items-center gap-4 text-[10px] text-slate-500">
              <span>Confiança:</span>
              {Object.entries(COR_CONFIANCA).map(([k, v]) => (
                <span key={k} className={`flex items-center gap-1 ${v.texto}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />
                  {v.rotulo}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CampoIA rotulo="Tag do Equipamento" campo={dadosIA?.tagEquipamento} />
              <CampoIA rotulo="Nº Certificado" campo={dadosIA?.numeroCertificado} />
              <CampoIA rotulo="Data da Calibração" campo={dadosIA?.dataCalibracao} />
              <CampoIA rotulo="Próxima Calibração" campo={dadosIA?.proximaData} />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Resultado</div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${dadosIA?.resultado?.valor ? 'text-slate-200' : 'text-slate-600 italic'}`}>
                    {dadosIA?.resultado?.valor
                      ? LABEL_RESULTADO_IA[dadosIA.resultado.valor] || dadosIA.resultado.valor
                      : 'Não encontrado'}
                  </span>
                  {dadosIA?.resultado?.confianca && <BadgeConfianca nivel={dadosIA.resultado.confianca} />}
                </div>
              </div>
              <CampoIA rotulo="Fornecedor / Laboratório" campo={dadosIA?.fornecedor} />
            </div>

            {(p.descricao?.valor || p.numeroCertificado?.valor || p.validoAte?.valor) && (
              <div className="bg-[var(--surface-1)] rounded-lg px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Padrão de Rastreabilidade</div>
                <div className="grid grid-cols-3 gap-2">
                  <CampoIA rotulo="Descrição" campo={p.descricao} />
                  <CampoIA rotulo="Nº Certificado" campo={p.numeroCertificado} />
                  <CampoIA rotulo="Válido até" campo={p.validoAte} />
                </div>
              </div>
            )}

            {grupos.length > 0 ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  Medições — {grupos.length} grandeza(s) · {grupos.reduce((s, g) => s + g.pontos.length, 0)} ponto(s)
                </div>
                <div className="space-y-2">
                  {grupos.map((g, gi) => (
                    <div key={gi} className="bg-[var(--surface-1)] rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-medium text-slate-300">{g.nome}</span>
                        <span className="text-[10px] text-slate-500">({g.unidade})</span>
                        <span className="text-[10px] text-slate-600 ml-auto">{g.pontos.length} pontos</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-slate-600 border-b border-[var(--surface-3)]">
                              <th className="text-left pb-1 pr-3">Aplicado</th>
                              <th className="text-left pb-1 pr-3">Lido</th>
                              <th className="text-left pb-1 pr-3">Erro</th>
                              <th className="text-left pb-1">Incerteza</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.pontos.map((pt, pi) => (
                              <tr key={pi} className="border-b border-[var(--surface-3)]/40 last:border-0">
                                <td className="py-0.5 pr-3 text-slate-400">{pt.aplicado}</td>
                                <td className="py-0.5 pr-3 text-slate-400">{pt.lido}</td>
                                <td className={`py-0.5 pr-3 font-mono font-medium ${
                                  pt.erro == null ? 'text-slate-600' :
                                  Math.abs(pt.erro) === 0 ? 'text-emerald-400' : 'text-slate-300'
                                }`}>
                                  {pt.erro != null ? (pt.erro >= 0 ? '+' : '') + pt.erro.toFixed(4) : '—'}
                                </td>
                                <td className="py-0.5 text-slate-500">
                                  {pt.incerteza != null ? `U=${pt.incerteza}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-amber-950/20 border border-amber-800/30 rounded-lg px-3 py-2.5 text-xs text-amber-400/80">
                ℹ️ Nenhuma tabela de medições detectada. Os pontos podem ser inseridos manualmente no formulário.
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--surface-4)] bg-[var(--surface-2)]">
            <p className="text-[10px] text-slate-600">
              Revise os dados antes de aplicar. Baixa confiança = verificar manualmente.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEstadoScan(null)}
                className="px-3 py-1.5 text-xs text-slate-400 border border-[var(--surface-4)] rounded-lg hover:text-slate-200 transition-colors"
              >
                Ignorar
              </button>
              <button
                type="button"
                onClick={() => handleAplicarDadosIA(dadosIA)}
                className="px-4 py-1.5 text-xs font-semibold bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors"
              >
                ✓ Aplicar tudo
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Padrão: preview do PDF
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--surface-4)] bg-[var(--surface-2)]">
          <span className="text-xs text-slate-400 truncate max-w-[60%]" title={arquivoCertificado?.name}>
            📄 {arquivoCertificado?.name}
          </span>
          <button
            type="button"
            onClick={iniciarScan}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-medium bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          >
            🤖 Analisar com IA
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-[var(--surface-1)]">
          {previewUrl && (
            isImagem ? (
              <img src={previewUrl} alt="Certificado" className="w-full h-full object-contain p-4" />
            ) : (
              <embed src={previewUrl} type="application/pdf" className="w-full h-full" />
            )
          )}
        </div>
      </div>
    );
  }

  // ─── Seções do formulário (reutilizadas em ambos os layouts) ─────────────
  const seccoesFormulario = (
    <>
      {/* Popup para vincular / cadastrar padrão de referência */}
      {padraoPickerIdx !== null && (
        <PadraoPickerPopup
          idx={padraoPickerIdx}
          padroesReferencia={padroesReferencia}
          laboratorios={laboratorios}
          onVincular={(pr) => vincularPadrao(padraoPickerIdx, pr)}
          onFechar={() => setPadraoPickerIdx(null)}
        />
      )}

      {/* Progresso do formulário */}
      {(form.instrumentId || form.data) && (() => {
        const totalMedicoes = form.gruposMedicao.reduce((s, g) => s + g.pontos.length, 0);
        const medicoesFeitas = form.gruposMedicao.reduce((s, g) => s + g.pontos.filter(p => p.lido !== '' && p.lido !== null && p.lido !== undefined).length, 0);
        const temPadrao = form.padroesUsados.some(p => p.padraoReferenciaId || p.descricao.trim());
        const etapas = [
          { ok: !!form.instrumentId, label: 'Instrumento' },
          { ok: !!form.data, label: 'Data' },
          ...(ehInterna ? [{ ok: temPadrao, label: 'Padrão' }] : []),
          ...(ehInterna && totalMedicoes > 0 ? [{ ok: medicoesFeitas === totalMedicoes && totalMedicoes > 0, label: `Medições ${medicoesFeitas}/${totalMedicoes}` }] : []),
          { ok: !!form.resultado, label: 'Resultado' },
          { ok: !!form.proximaData, label: 'Próx. data' },
        ];
        const feitos = etapas.filter(e => e.ok).length;
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {etapas.map((e, i) => (
              <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                e.ok ? 'bg-emerald-950/40 border-emerald-700/50 text-emerald-300' : 'bg-[var(--surface-3)] border-[var(--surface-4)] text-slate-500'
              }`}>
                {e.ok ? '✓' : '○'} {e.label}
              </span>
            ))}
            <span className="text-[11px] text-slate-600 ml-auto">{feitos}/{etapas.length}</span>
          </div>
        );
      })()}

      {/* Erro global */}
      {erro && (
        <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-lg px-4 py-3">
          {erro}
        </div>
      )}

      {/* Painel de campos faltando (ao tentar concluir/revisar sem dados) */}
      {camposFaltando.length > 0 && (
        <div className="bg-amber-950/40 border border-amber-700/60 rounded-lg px-4 py-3">
          <p className="text-sm font-semibold text-amber-300 mb-2">
            Preencha os campos obrigatórios antes de concluir:
          </p>
          <ul className="space-y-1">
            {camposFaltando.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById(item.id);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      el.classList.add('ring-2', 'ring-amber-500', 'ring-offset-2', 'ring-offset-transparent', 'rounded-lg');
                      setTimeout(() => el.classList.remove('ring-2', 'ring-amber-500', 'ring-offset-2', 'ring-offset-transparent', 'rounded-lg'), 3000);
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-200 underline underline-offset-2 transition-colors"
                >
                  <span className="text-amber-600">→</span>
                  {item.rotulo}
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-amber-600 mt-2">
            Clique em cada item para ir ao campo. Ou use "Salvar Rascunho" para guardar o progresso sem validação.
          </p>
        </div>
      )}

      {/* Seção 1: Tipo de calibração */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
          Tipo de Calibração
        </h3>
        {somenteExterna ? (
          <div className="flex items-center gap-3 py-3 px-4 rounded-lg border bg-blue-700 border-blue-500 text-white">
            <span className="text-sm font-medium">🏭 Calibração Externa</span>
            <span className="text-xs opacity-75 ml-auto">Plano da empresa: somente externa</span>
          </div>
        ) : (
          <div className="flex gap-3">
            {['externa', 'interna'].map((tipo) => (
              <button
                key={tipo}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, calibrationType: tipo }))}
                className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
                  form.calibrationType === tipo
                    ? tipo === 'externa'
                      ? 'bg-blue-700 border-blue-500 text-white shadow-lg shadow-blue-900/30'
                      : 'bg-indigo-700 border-indigo-500 text-white shadow-lg shadow-indigo-900/30'
                    : 'bg-[var(--surface-3)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'
                }`}
              >
                {tipo === 'externa' ? '🏭 Calibração Externa' : '🔧 Calibração Interna'}
                <span className="block text-xs font-normal mt-0.5 opacity-75">
                  {tipo === 'externa' ? 'Laboratório externo (RBC ou não-RBC)' : 'Laboratório / técnico interno'}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Seção 2: Instrumento */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
          Instrumento
        </h3>
        <div>
          <label className="block text-slate-300 mb-1">
            Instrumento <span className="text-red-400">*</span>
          </label>
          {carregandoInstrumentos ? (
            <div className="h-10 bg-[var(--surface-4)] rounded-lg animate-pulse" />
          ) : (
            <select
              name="instrumentId"
              value={form.instrumentId}
              onChange={handleChange}
              disabled={ehEdicao}
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 disabled:opacity-50"
            >
              <option value="">Selecione um instrumento...</option>
              {instrumentos.map((inst) => (
                <option key={inst._id} value={inst._id}>
                  {inst.tag} — {inst.descricao || inst.description}
                </option>
              ))}
            </select>
          )}
          {ehEdicao && (
            <p className="text-xs text-slate-500 mt-1">O instrumento não pode ser alterado após a criação.</p>
          )}
        </div>
      </section>

      {/* Seção 3: Datas e Resultado */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
          Dados da Calibração
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-slate-300 mb-1">
              Data da Calibração <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              name="data"
              value={form.data}
              onChange={handleChange}
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div id="campo-proxima-data">
            <label className="block text-slate-300 mb-1">
              Próxima Calibração
              {(() => {
                const inst = instrumentos.find(i => i._id === form.instrumentId);
                const freq = inst?.calibrationFrequencyDays ?? inst?.instrumentClass?.calibrationFrequencyDays;
                return freq ? <span className="ml-2 text-[10px] text-blue-400">freq: {freq}d</span> : null;
              })()}
            </label>
            <input
              type="date"
              name="proximaData"
              value={form.proximaData}
              onChange={handleChange}
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
            />
            {proximaDataSugerida && proximaDataSugerida.data !== form.proximaData && (
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, proximaData: proximaDataSugerida.data }))}
                className="mt-1.5 flex items-center gap-1.5 text-[11px] text-blue-400 hover:text-blue-300 bg-blue-950/30 hover:bg-blue-950/50 border border-blue-800/40 px-2.5 py-1 rounded-full transition-colors"
              >
                <span>📅</span>
                <span>Usar sugestão: <strong>{proximaDataSugerida.label}</strong></span>
                <span className="text-blue-600">· {proximaDataSugerida.freq}d</span>
              </button>
            )}
          </div>
          <div id="campo-resultado">
            <label className="block text-slate-300 mb-1">
              Resultado
            </label>
            <select
              name="resultado"
              value={form.resultado}
              onChange={handleChange}
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
            >
              <option value="">Selecione...</option>
              {RESULTADOS.map((r) => (
                <option key={r.valor} value={r.valor}>{r.rotulo}</option>
              ))}
            </select>
            {sugestaoResultado && sugestaoResultado !== form.resultado && (
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, resultado: sugestaoResultado }))}
                className="mt-1.5 text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
              >
                💡 Sugestão pelo EMP: {RESULTADOS.find(r => r.valor === sugestaoResultado)?.rotulo}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Seção 4: Fornecedor (apenas externa) */}
      {!ehInterna && (
        <section id="campo-fornecedor" className="bg-blue-950/20 border border-blue-900/40 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-3">
            Informações do Laboratório Externo
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-1">
                Fornecedor / Laboratório
              </label>
              <input
                type="text"
                name="fornecedor"
                list="laboratorios-datalist"
                value={form.fornecedor}
                onChange={handleChange}
                placeholder="Ex: Lab Metrologia São Paulo Ltda"
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              <datalist id="laboratorios-datalist">
                {laboratorios.map((lab) => (
                  <option key={lab.id} value={lab.nome} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-slate-300 mb-1">Acreditação</label>
              <select
                name="acreditacaoFornecedor"
                value={form.acreditacaoFornecedor}
                onChange={handleChange}
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
              >
                <option value="">Selecione...</option>
                {ACREDITACOES.map((a) => (
                  <option key={a.valor} value={a.valor}>{a.rotulo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-300 mb-1">Número do Certificado</label>
              <input
                type="text"
                name="numeroCertificado"
                value={form.numeroCertificado}
                onChange={handleChange}
                placeholder="Ex: CERT-2024-00312"
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-1">Arquivo do Certificado (PDF)</label>
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  setArquivoCertificado(e.target.files[0] || null);
                  setEstadoScan(null);
                }}
                className="w-full text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[var(--surface-4)] file:text-slate-300 hover:file:bg-[#2d3748] file:cursor-pointer"
              />
              {arquivoCertificado && (
                <p className="text-xs text-indigo-400 mt-1">
                  → PDF visível à direita · use "Analisar com IA" para extração automática
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Seção 5: Padrões de rastreabilidade (múltiplos — ISO 10012 cláusula 7.5) */}
      <section id="campo-padroes">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
            Padrões de Rastreabilidade Utilizados
          </h3>
          <button
            type="button"
            onClick={addPadrao}
            className="text-xs px-2.5 py-1 bg-[var(--surface-4)] border border-[var(--surface-5)] text-slate-300 rounded-lg hover:border-blue-600 hover:text-blue-400 transition-colors"
          >
            + Adicionar padrão
          </button>
        </div>
        {/* ── Calibração interna: seletor por tag ── */}
        {ehInterna ? (
          <div className="space-y-2">
            {form.padroesUsados.map((p, idx) => {
              const pr = padroesReferencia.find(x => (x.id || x._id) === p.padraoReferenciaId);
              const vencido = pr?.nextCalibrationDate && new Date(pr.nextCalibrationDate) < new Date();
              return (
                <div key={idx} className={`border rounded-lg p-3 transition-colors ${pr ? (vencido ? 'bg-red-950/20 border-red-800/50' : 'bg-indigo-950/20 border-indigo-800/40') : 'bg-[var(--surface-3)] border-[var(--surface-4)]'}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-slate-400 text-xs mb-1">TAG do Padrão {idx + 1}</label>
                      <input
                        type="text"
                        list={`padroes-list-${idx}`}
                        value={pr?.tag || p.descricao || ''}
                        onChange={e => {
                          const tag = e.target.value.trim();
                          const encontrado = padroesReferencia.find(x => x.tag?.toLowerCase() === tag.toLowerCase());
                          if (encontrado) {
                            vincularPadrao(idx, encontrado);
                          } else {
                            updatePadrao(idx, 'descricao', tag);
                            updatePadrao(idx, 'padraoReferenciaId', null);
                          }
                        }}
                        placeholder="Ex: PAD-001"
                        className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500 text-sm font-mono"
                      />
                      <datalist id={`padroes-list-${idx}`}>
                        {padroesReferencia.map(x => <option key={x.id || x._id} value={x.tag}>{x.tag} — {x.descricao}</option>)}
                      </datalist>
                    </div>
                    {form.padroesUsados.length > 1 && (
                      <button type="button" onClick={() => removePadrao(idx)} className="text-xs text-red-400 hover:text-red-300 mt-5 flex-shrink-0">✕</button>
                    )}
                  </div>
                  {pr ? (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                      <div><span className="text-slate-500">Descrição</span><div className="text-slate-200 truncate">{pr.descricao}</div></div>
                      <div><span className="text-slate-500">Certificado</span><div className="text-slate-200 font-mono">{pr.numeroCertificado || '—'}</div></div>
                      <div><span className="text-slate-500">Válido até</span><div className={vencido ? 'text-red-400 font-semibold' : 'text-slate-200'}>{pr.nextCalibrationDate ? new Date(pr.nextCalibrationDate).toLocaleDateString('pt-BR') : '—'}{vencido ? ' ⚠ Vencido' : ''}</div></div>
                      <div><span className="text-slate-500">U / k</span><div className="text-slate-200">{pr.incertezaExpandida != null ? `${pr.incertezaExpandida} / k=${pr.fatorK ?? 2}` : '—'}</div></div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-amber-500 mt-1">TAG não encontrada nos padrões cadastrados. Cadastre o padrão em Padrões de Referência primeiro.</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
        /* ── Calibração externa: formulário completo ── */
        <div className="space-y-3">
          {form.padroesUsados.map((p, idx) => (
            <div key={idx} className={`border rounded-lg p-4 transition-colors ${p.padraoReferenciaId ? 'bg-blue-950/20 border-blue-900/40' : 'bg-[var(--surface-3)] border-[var(--surface-4)]'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-medium">Padrão {idx + 1}</span>
                  {p.padraoReferenciaId && (
                    <span className="text-[10px] text-blue-400 bg-blue-900/30 border border-blue-800/40 px-1.5 py-0.5 rounded-full">🔗 Vinculado</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setPadraoPickerIdx(idx)}
                    className="text-xs px-2 py-1 bg-[var(--surface-4)] border border-[var(--surface-5)] text-blue-400 rounded-lg hover:border-blue-600 transition-colors">
                    🔗 Vincular padrão
                  </button>
                  {form.padroesUsados.length > 1 && (
                    <button type="button" onClick={() => removePadrao(idx)} className="text-xs text-red-400 hover:text-red-300 transition-colors">Remover</button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-slate-400 text-xs mb-1">Descrição do Padrão</label>
                  <input type="text" value={p.descricao} onChange={e => updatePadrao(idx, 'descricao', e.target.value)}
                    placeholder="Ex: Manômetro padrão RBC nº 0312"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Nº Certificado</label>
                  <input type="text" value={p.numeroCertificado} onChange={e => updatePadrao(idx, 'numeroCertificado', e.target.value)}
                    placeholder="Ex: RBC-0312/2024"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Válido até</label>
                  <input type="date" value={p.validoAte} onChange={e => updatePadrao(idx, 'validoAte', e.target.value)}
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Incerteza Expandida U</label>
                  <input type="number" step="any" value={p.incertezaExpandida} onChange={e => updatePadrao(idx, 'incertezaExpandida', e.target.value)}
                    placeholder="Ex: 0.05"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Fator k</label>
                  <input type="number" step="0.1" value={p.fatorK} onChange={e => updatePadrao(idx, 'fatorK', e.target.value)}
                    placeholder="2"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </section>

      {/* Seção 6: Medições por grandeza */}
      {ehInterna && form.gruposMedicao.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
            Medições por Grandeza
          </h3>
          <div className="space-y-4">
            {form.gruposMedicao.map((grupo, gi) => {
              const getU = makeGetU(grupo);
              const resumo = resumoGrupo(grupo, getU);
              const maiorErro = maiorErroGrupo(grupo, getU);
              const todosPreenchidos = resumo.pendentes === 0 && resumo.total > 0;
              const temReprovado = resumo.reprovados > 0;

              return (
                <div
                  key={gi}
                  className={`border rounded-lg p-4 transition-colors ${
                    !todosPreenchidos
                      ? 'bg-[var(--surface-3)] border-[var(--surface-4)]'
                      : temReprovado
                      ? 'bg-red-950/20 border-red-800/50'
                      : 'bg-emerald-950/20 border-emerald-800/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-slate-200">
                        {grupo.nome}
                        <span className="ml-1.5 text-slate-400 font-normal text-xs">({grupo.unidade})</span>
                      </h4>
                      {(() => {
                        const prGrupo = padroesReferencia.find(x => (x.id || x._id) === grupo.padraoReferenciaId);
                        return (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              list={`padroes-grupo-${gi}`}
                              value={prGrupo?.tag || ''}
                              onChange={e => {
                                const tag = e.target.value.trim();
                                const encontrado = padroesReferencia.find(x => x.tag?.toLowerCase() === tag.toLowerCase());
                                if (encontrado) vincularPadraoGrupo(gi, encontrado);
                                else if (!tag) vincularPadraoGrupo(gi, { id: null });
                              }}
                              placeholder="Padrão (TAG)"
                              className={`w-32 text-xs font-mono px-2 py-1 rounded border focus:outline-none focus:border-indigo-500 ${prGrupo ? 'bg-indigo-950/30 border-indigo-700/50 text-slate-200' : 'bg-[var(--surface-4)] border-[var(--surface-5)] text-slate-400 placeholder-slate-600'}`}
                            />
                            <datalist id={`padroes-grupo-${gi}`}>
                              {padroesReferencia.map(x => <option key={x.id || x._id} value={x.tag}>{x.tag} — {x.descricao}</option>)}
                            </datalist>
                            {prGrupo && (
                              <span className="text-[10px] text-indigo-400">U={prGrupo.incertezaExpandida ?? '?'}/k={prGrupo.fatorK ?? 2}</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Toggle: padrão único para o grupo vs. padrão por ponto */}
                      <label className="flex items-center gap-1 cursor-pointer" title="Ativar para usar padrões diferentes em cada ponto">
                        <input
                          type="checkbox"
                          checked={!!grupo.usarPadraoPorPonto}
                          onChange={() => togglePadraoPorPonto(gi)}
                          className="w-3 h-3 accent-amber-500"
                        />
                        <span className={`text-[11px] ${grupo.usarPadraoPorPonto ? 'text-amber-400' : 'text-slate-500'}`}>
                          Padrão por ponto
                        </span>
                      </label>
                      <select
                        value={grupo.numLeituras || 1}
                        onChange={e => handleNumLeiturasChange(gi, e.target.value)}
                        className="text-xs bg-[var(--surface-4)] border border-[var(--surface-5)] rounded px-2 py-1 text-slate-300"
                        title="Número de leituras por ponto"
                      >
                        <option value={1}>1 leitura/ponto</option>
                        <option value={3}>3 leituras/ponto</option>
                        <option value={5}>5 leituras/ponto</option>
                        <option value={10}>10 leituras/ponto</option>
                      </select>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                        grupo.empTipo === 'text'
                          ? 'bg-slate-800 text-slate-300 border-slate-700'
                          : 'bg-blue-950/60 text-blue-300 border-blue-800/60'
                      }`}>
                        EMP {rotuloEMP(grupo)}
                      </span>
                      {(resumo.aprovados > 0 || resumo.reprovados > 0) && (
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                          temReprovado
                            ? 'bg-red-900/40 text-red-300 border-red-800/50'
                            : 'bg-emerald-900/40 text-emerald-300 border-emerald-800/50'
                        }`}>
                          {resumo.aprovados}/{resumo.total - resumo.pendentes} pts ✓
                        </span>
                      )}
                      {maiorErro !== null && (
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                          maiorErro.dentro
                            ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800/50'
                            : 'bg-red-900/40 text-red-300 border-red-800/50'
                        }`} title="Maior erro absoluto observado vs EMP">
                          ↑{maiorErro.max.toFixed(4)} {grupo.unidade} {maiorErro.dentro ? '≤' : '>'} ±{grupo.empValor}
                        </span>
                      )}
                    </div>
                  </div>

                  {grupo.empTipo === 'text' && (
                    <div className="mb-3 text-xs text-amber-400/80 bg-amber-950/20 border border-amber-800/30 rounded px-3 py-1.5">
                      ℹ️ EMP definido como texto — aprovação avaliada visualmente pelo técnico.
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-[var(--surface-4)]">
                          <th className="w-5" />
                          <th className="text-left py-2 pr-3 font-medium">Aplicado ({grupo.unidade})</th>
                          <th className="text-left py-2 pr-3 font-medium">
                            {(grupo.numLeituras || 1) > 1 ? `Leituras × ${grupo.numLeituras} / Média` : `Lido (${grupo.unidade})`}
                          </th>
                          <th className="text-left py-2 pr-3 font-medium">Erro</th>
                          <th className="text-left py-2 pr-3 font-medium">Status</th>
                          {ehInterna && grupo.usarPadraoPorPonto && (
                            <th className="text-left py-2 pr-3 font-medium">Padrão</th>
                          )}
                          <th className="text-left py-2 font-medium">
                            {ehInterna ? 'U (GUM, k=2)' : 'Incerteza / u_A'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.pontos.map((ponto, pi) => {
                          const dentroEMP = verificarEMPPorPonto(ponto.erro, ponto.aplicado, grupo, getU(ponto));
                          const lidoPreenchido = ponto.lido !== '' && ponto.lido !== null && ponto.lido !== undefined;

                          return (
                            <tr key={pi} className="border-b border-[var(--surface-4)]/50 last:border-0">
                              <td className="py-1.5 pr-1 w-5">
                                <button
                                  type="button"
                                  onClick={() => removerPonto(gi, pi)}
                                  className="text-slate-700 hover:text-red-400 text-xs leading-none transition-colors"
                                  title="Remover ponto"
                                >×</button>
                              </td>
                              <td className="py-1.5 pr-3">
                                <div className="flex items-center gap-1">
                                  {ponto._dir && (
                                    <span className={`text-xs font-bold flex-shrink-0 ${ponto._dir === '↑' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                      {ponto._dir}
                                    </span>
                                  )}
                                  <input
                                    type="number"
                                    step="any"
                                    value={ponto.aplicado}
                                    onChange={(e) => handlePontoChange(gi, pi, 'aplicado', e.target.value)}
                                    className="w-24 bg-[var(--surface-4)] border border-transparent rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-blue-500"
                                  />
                                </div>
                              </td>
                              <td className="py-1.5 pr-3">
                                {(grupo.numLeituras || 1) > 1 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex gap-1 flex-wrap">
                                      {Array.from({ length: grupo.numLeituras }).map((_, li) => (
                                        <input
                                          key={li}
                                          type="number"
                                          step="any"
                                          value={(ponto.leituras || [])[li] ?? ''}
                                          onChange={e => handleLeituraChange(gi, pi, li, e.target.value)}
                                          placeholder={`L${li + 1}`}
                                          className="w-16 bg-[var(--surface-4)] border border-transparent rounded px-1.5 py-1 text-slate-200 focus:outline-none focus:border-blue-500 text-xs"
                                        />
                                      ))}
                                    </div>
                                    {lidoPreenchido && (
                                      <div className="text-[10px] text-slate-400 font-mono">
                                        x̄ = {Number(ponto.lido).toFixed(4)}
                                        {ponto.uA !== null && ponto.uA !== undefined && (
                                          <span className="ml-2 text-blue-400"> u_A = {ponto.uA.toFixed(4)}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    step="any"
                                    value={ponto.lido}
                                    onChange={(e) => handlePontoChange(gi, pi, 'lido', e.target.value)}
                                    className={`w-24 rounded px-2 py-1 text-slate-200 focus:outline-none border transition-colors ${
                                      !lidoPreenchido || dentroEMP === null
                                        ? 'bg-[var(--surface-4)] border-transparent focus:border-blue-500'
                                        : dentroEMP
                                        ? 'bg-emerald-950/30 border-emerald-600 focus:border-emerald-500'
                                        : 'bg-red-950/30 border-red-600 focus:border-red-500'
                                    }`}
                                  />
                                )}
                              </td>
                              <td className="py-1.5 pr-3">
                                <span className={`font-mono font-semibold ${
                                  dentroEMP === null
                                    ? 'text-slate-500'
                                    : dentroEMP
                                    ? 'text-emerald-400'
                                    : 'text-red-400'
                                }`}>
                                  {ponto.erro !== null && ponto.erro !== undefined
                                    ? (ponto.erro >= 0 ? '+' : '') + Number(ponto.erro).toFixed(4)
                                    : '—'}
                                </span>
                              </td>
                              <td className="py-1.5 pr-3">
                                {dentroEMP === null ? (
                                  <span className="text-slate-600 text-xs">—</span>
                                ) : dentroEMP ? (
                                  <span className="text-emerald-400 text-sm" title="Dentro do EMP">✅</span>
                                ) : (
                                  <span className="text-red-400 text-sm" title="Fora do EMP">❌</span>
                                )}
                              </td>
                              {/* Coluna de padrão por ponto (só quando toggle ativo) */}
                              {ehInterna && grupo.usarPadraoPorPonto && (() => {
                                const prPonto = padroesReferencia.find(x => (x.id || x._id) === ponto.padraoReferenciaId);
                                return (
                                  <td className="py-1.5 pr-2">
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="text"
                                        list={`padroes-ponto-${gi}-${pi}`}
                                        value={prPonto?.tag || ''}
                                        onChange={e => {
                                          const tag = e.target.value.trim();
                                          const enc = padroesReferencia.find(x => x.tag?.toLowerCase() === tag.toLowerCase());
                                          if (enc) vincularPadraoPonto(gi, pi, enc);
                                          else if (!tag) vincularPadraoPonto(gi, pi, { id: null });
                                        }}
                                        placeholder="TAG"
                                        className={`w-24 text-[11px] font-mono px-1.5 py-1 rounded border focus:outline-none focus:border-amber-500 ${prPonto ? 'bg-amber-950/30 border-amber-700/50 text-slate-200' : 'bg-[var(--surface-4)] border-[var(--surface-5)] text-slate-500 placeholder-slate-600'}`}
                                      />
                                      <datalist id={`padroes-ponto-${gi}-${pi}`}>
                                        {padroesReferencia.map(x => <option key={x.id || x._id} value={x.tag}>{x.tag} — {x.descricao}</option>)}
                                      </datalist>
                                    </div>
                                    {prPonto && (
                                      <div className="text-[10px] text-amber-400 font-mono mt-0.5">
                                        U={prPonto.incertezaExpandida ?? '?'}/k={prPonto.fatorK ?? 2}
                                      </div>
                                    )}
                                  </td>
                                );
                              })()}
                              <td className="py-1.5">
                                {ehInterna ? (() => {
                                  // Padrão efetivo: por ponto (quando toggle ativo) > padrão do grupo
                                  const padraoIdEfetivo = (grupo.usarPadraoPorPonto && ponto.padraoReferenciaId)
                                    ? ponto.padraoReferenciaId
                                    : grupo.padraoReferenciaId;
                                  const padraoGrupo = padraoIdEfetivo ? padroesReferencia.find(x => (x.id || x._id) === padraoIdEfetivo) : null;
                                  const gum = calcularGUM(ponto.uA, padraoGrupo ? [padraoGrupo] : form.padroesUsados);
                                  if (!gum) return (
                                    <span className="text-xs text-slate-600 font-mono" title="Informe U e k do padrão para calcular">—</span>
                                  );
                                  return (
                                    <div>
                                      <span className="font-mono text-sm text-slate-200">
                                        {gum.U.toFixed(4)}
                                      </span>
                                      <div className="text-[10px] font-mono text-slate-500 mt-0.5 space-y-0.5">
                                        {ponto.uA != null && <div className="text-blue-400">u_A={ponto.uA.toFixed(4)}</div>}
                                        <div>u_B={gum.uB.toFixed(4)}</div>
                                        <div>u_c={gum.uc.toFixed(4)}</div>
                                        <div className="text-slate-300">U=k·u_c={gum.kCal}×{gum.uc.toFixed(4)}</div>
                                      </div>
                                    </div>
                                  );
                                })() : (
                                  <>
                                    <input
                                      type="number"
                                      step="any"
                                      value={ponto.incerteza}
                                      onChange={(e) => handlePontoChange(gi, pi, 'incerteza', e.target.value)}
                                      placeholder="U="
                                      className="w-24 bg-[var(--surface-4)] border border-transparent rounded px-2 py-1 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                                    />
                                    {ponto.uA !== null && ponto.uA !== undefined && (
                                      <div className="text-[10px] text-blue-400 font-mono mt-0.5">u_A={ponto.uA.toFixed(4)}</div>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button
                    type="button"
                    onClick={() => adicionarPonto(gi)}
                    className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    + Ponto
                  </button>

                  {todosPreenchidos && grupo.empTipo !== 'text' && (
                    <div className={`mt-3 pt-3 border-t text-xs font-medium flex items-center gap-1.5 ${
                      temReprovado
                        ? 'border-red-800/30 text-red-400'
                        : 'border-emerald-800/30 text-emerald-400'
                    }`}>
                      {temReprovado ? '❌' : '✅'}
                      {temReprovado
                        ? `${resumo.reprovados} ponto(s) fora do EMP nesta grandeza`
                        : `Todos os ${resumo.total} pontos dentro do EMP`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {resumoGeral && resumoGeral.total > 0 && resumoGeral.pendentes < resumoGeral.total && (
            <div className={`mt-3 rounded-lg px-4 py-3 border flex items-center justify-between ${
              resumoGeral.reprovados > 0
                ? 'bg-red-950/30 border-red-800/50 text-red-300'
                : 'bg-emerald-950/30 border-emerald-800/50 text-emerald-300'
            }`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                {resumoGeral.reprovados > 0 ? '❌' : '✅'}
                <span>
                  {resumoGeral.aprovados} de {resumoGeral.total - resumoGeral.pendentes} pontos avaliados dentro do EMP
                  {resumoGeral.pendentes > 0 && ` · ${resumoGeral.pendentes} pendente(s)`}
                </span>
              </div>
              {sugestaoResultado && (
                <span className="text-xs opacity-75">
                  Resultado sugerido: <strong>{RESULTADOS.find(r => r.valor === sugestaoResultado)?.rotulo}</strong>
                </span>
              )}
            </div>
          )}
        </section>
      )}

      {/* Seção 7: Procedimento (POP) */}
      {procedimentos.length > 0 && (
        <section>
          <label className="block text-slate-300 mb-1">
            Procedimento (POP)
            <span className="ml-2 text-xs text-slate-500 font-normal">opcional</span>
          </label>
          <select
            name="procedimentoId"
            value={form.procedimentoId}
            onChange={handleChange}
            className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
          >
            <option value="">— Nenhum —</option>
            {procedimentos.map((p) => (
              <option key={p.id || p._id} value={p.id || p._id}>
                {p.numero} — {p.descricao}
              </option>
            ))}
          </select>
          {form.procedimentoId && (() => {
            const proc = procedimentos.find(p => (p.id || p._id) === form.procedimentoId);
            return proc?.arquivoUrl ? (
              <a
                href={proc.arquivoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-400 hover:text-emerald-300"
              >
                📄 Visualizar PDF do POP
              </a>
            ) : null;
          })()}
        </section>
      )}

      {/* Seção 8: Observações */}
      <section>
        <label className="block text-slate-300 mb-1">Observações</label>
        <textarea
          name="observacoes"
          value={form.observacoes}
          onChange={handleChange}
          rows={3}
          placeholder="Condições ambientais, restrições de uso, notas técnicas..."
          className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
        />
      </section>

      {/* Ações */}
      <div className="pt-2 space-y-2">
        {!ehEdicao && (autoSalvando || ultimoAutoSave) && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            {autoSalvando ? (
              <>
                <div className="w-3 h-3 border border-slate-500 border-t-transparent rounded-full animate-spin" />
                <span>Salvando rascunho...</span>
              </>
            ) : (
              <span>Rascunho salvo às {ultimoAutoSave.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancelar}
            className="flex-1 py-2.5 px-4 rounded-lg border border-[var(--surface-4)] text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors"
          >
            Cancelar
          </button>
          {!ehEdicao && (
            <button
              type="button"
              disabled={carregando}
              onClick={() => handleSubmit(null, true)}
              className="py-2.5 px-4 rounded-lg border border-[var(--surface-4)] text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
              title="Salva sem validação completa — pode ser finalizado depois"
            >
              Rascunho
            </button>
          )}
          <button
            type="submit"
            disabled={carregando}
            className="flex-1 py-2.5 px-4 rounded-lg bg-blue-700 hover:bg-blue-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {carregando ? 'Salvando...' : ehEdicao ? 'Salvar Alterações' : 'Finalizar'}
          </button>
        </div>
      </div>
    </>
  );

  // ─── Layout split (quando há arquivo selecionado) ─────────────────────────
  if (arquivoCertificado) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-[var(--surface-1)]">
        {/* Barra de título */}
        <div className="flex items-center justify-between px-5 py-3 bg-[var(--surface-2)] border-b border-[var(--surface-4)] flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-slate-100">
              {ehEdicao ? 'Editar Calibração' : 'Nova Calibração'} <span className="text-[10px] text-yellow-400">v21</span>
            </h2>
            <span className="text-xs text-slate-500 hidden sm:inline">
              — preencha o formulário consultando o PDF ao lado
            </span>
          </div>
          <button
            type="button"
            onClick={onCancelar}
            className="text-slate-500 hover:text-slate-200 text-xl leading-none transition-colors"
          >
            ×
          </button>
        </div>

        {/* Corpo split */}
        <div className="flex flex-1 overflow-hidden">
          {/* Esquerda: formulário */}
          <div className="w-1/2 overflow-y-auto border-r border-[var(--surface-4)]">
            <form onSubmit={handleSubmit} className="p-6 space-y-6 text-sm text-slate-200">
              {seccoesFormulario}
            </form>
          </div>

          {/* Direita: PDF / IA */}
          <div className="w-1/2 flex flex-col bg-[var(--surface-1)]">
            {renderPainelDireito()}
          </div>
        </div>
      </div>
    );
  }

  // ─── Layout normal (sem arquivo) ──────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-sm text-slate-200">
      {seccoesFormulario}
    </form>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatarDataInput(data) {
  if (!data) return '';
  return new Date(data).toISOString().split('T')[0];
}

function numSeguro(val, fallback) {
  if (typeof val === 'number' && !isNaN(val)) return val;
  if (val == null || val === '') return fallback;
  const parsed = parseFloat(String(val).replace(',', '.'));
  return isNaN(parsed) ? fallback : parsed;
}
